import { useEffect, useState } from 'react';
import { AiPromptModalBody } from '~/components/AiPromptForm/index.client';

import LoadingAnimation from '~/components/LoadingAnimation';
import type { LoaderFunctionArgs } from '@remix-run/node';

import { json, redirect } from '@remix-run/node';
import { prompts } from '~/services/index';
import { useSetApplicationContext } from '~/components/ApplicationContext';
import { useLoaderData } from '@remix-run/react';
import { LanguageProvider } from '~/contexts/LanguageContext';
import { detectBrowserLanguage } from '~/services/languageLoader.server';
import { AvailableLanguage, availableLanguages } from '~/services/i18n';
import { loadPromptTranslations } from '~/services/prompt-translations';
import SignInModal from '~/components/SiginModal';
import { message } from 'antd';
import { useSetMessagesAPI } from '~/hooks';

export async function loader(args: LoaderFunctionArgs) {
    const promptId = args.params.promptId;
    const prompt = prompts.find((p) => p.id === promptId);

    if (!prompt) {
        throw new Response(`Can not find prompt with id ${promptId}`, {
            status: 404,
        });
    }

    const url = new URL(args.request.url);
    const langParam = url.searchParams.get('lang');
    let language: AvailableLanguage = 'en';

    if (
        langParam &&
        availableLanguages.includes(langParam as AvailableLanguage)
    ) {
        language = langParam as AvailableLanguage;
    } else if (!langParam) {
        const acceptLanguage = args.request.headers.get('accept-language');
        language = detectBrowserLanguage(acceptLanguage);
        url.searchParams.set('lang', language);
        // Ensure HTTPS protocol for security
        url.protocol = 'https:';
        return redirect(url.toString());
    }

    let translations: Record<string, string> = {};

    // Preload translations if not English
    if (language !== 'en') {
        try {
            const { loadTranslations } = await import(
                '~/services/translation-loader'
            );
            translations = await loadTranslations(language);
        } catch (error) {
            // Silently handle translation loading errors
        }
    }

    // Translate the prompt if not English
    let translatedPrompt = prompt;
    if (language !== 'en') {
        try {
            const promptTranslations = await loadPromptTranslations(language);
            const promptTranslation = promptTranslations[promptId!];

            if (promptTranslation) {
                translatedPrompt = {
                    ...prompt,
                    seoTitle: promptTranslation.seoTitle || prompt.seoTitle,
                    seoDescription:
                        promptTranslation.seoDescription ||
                        prompt.seoDescription,
                    title: promptTranslation.title || prompt.title,
                    example: promptTranslation.example || prompt.example,
                    description:
                        promptTranslation.description || prompt.description,
                    textLabel: promptTranslation.textLabel || prompt.textLabel,
                    textPlaceholder:
                        promptTranslation.textPlaceholder ||
                        prompt.textPlaceholder,
                };
            }
        } catch (error) {
            // Silently handle prompt translation errors
            console.warn('Failed to load prompt translations:', error);
        }
    }

    return json({
        promptId,
        prompt: translatedPrompt,
        language,
        translations,
    });
}

export default function Index() {
    const { prompt, language, translations } = useLoaderData<any>();
    const [isFetching, setFetching] = useState(true);
    const setApplicationContext = useSetApplicationContext();

    useEffect(() => {
        async function initialize() {
            try {
                setApplicationContext({
                    account: null,
                    host: 'iframe',
                    platform: 'web',
                });
                setFetching(false);
            } catch (err) {
                console.error(err);
                return;
            }
        }

        initialize();
    }, [setApplicationContext]);

    const [messageApi, contextHolder] = message.useMessage();
    useSetMessagesAPI(messageApi);

    if (isFetching) {
        return (
            <LanguageProvider language={language} translations={translations}>
                <div className='h-full w-full'>
                    <LoadingAnimation />
                </div>
            </LanguageProvider>
        );
    }
    return (
        <LanguageProvider language={language} translations={translations}>
            <div className='my-8'>
                <AiPromptModalBody aiTemplate={prompt} isFreeMode={true} />
            </div>
            <SignInModal />
            {contextHolder}
        </LanguageProvider>
    );
}
